/**
 * CrewVideoPlayer ✦
 *
 * IntersectionObserver-driven autoplay video for the swipe feed
 * and crew profile previews. Plays muted + looped + inline. Pauses
 * the moment the card scrolls offscreen so phones don't burn
 * battery decoding three clips at once.
 *
 * Reports the clip when the user holds the long-press menu — uses
 * the same /api/crew/video/report endpoint the moderator queue
 * watches.
 *
 * Props:
 *   src         — mp4 URL (already 720×1280 from the upload pipeline)
 *   poster      — JPEG URL (server-generated 1st-frame thumb)
 *   targetKind  — 'opportunity' | 'profile' (used by the report flow)
 *   targetId    — opp.id or user_id
 *   overlay     — optional ReactNode rendered above the video gradient
 *                 (pay chips, status badges, etc.)
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Play, Warning, X as XIcon } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PINK = '#FF3CAC';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const CrewVideoPlayer = ({
  src,
  poster,
  targetKind = 'opportunity',
  targetId = '',
  overlay = null,
  rounded = 22,
  testId = 'crew-video-player',
}) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reported, setReported] = useState(false);

  // IntersectionObserver — autoplay > 60% on screen, pause when out.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setInView(entry.isIntersecting && entry.intersectionRatio > 0.55));
      },
      { threshold: [0, 0.55, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      v.muted = true; // browsers require muted for autoplay
      const p = v.play();
      if (p && p.then) p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  }, [inView]);

  const reportClip = async () => {
    setReported(true);
    setReportOpen(false);
    try {
      await axios.post(`${API}/crew/video/report`, {
        target_kind: targetKind,
        target_id: targetId,
        reason: 'flagged from player',
      });
    } catch { /* silent — UI already shows confirmation */ }
  };

  if (!src) return null;

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      style={{
        position: 'relative', width: '100%', aspectRatio: '9 / 16',
        borderRadius: rounded, overflow: 'hidden', background: '#06061A',
      }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        data-testid={`${testId}-video`}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Soft top + bottom gradient for text readability */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(7,7,26,0.45) 0%, transparent 25%, transparent 65%, rgba(7,7,26,0.85) 100%)',
      }} />

      {/* Play hint when paused (offscreen) */}
      {!playing && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(7,7,26,0.55)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.15)',
          }}>
            <Play size={22} weight="fill" color="#fff" />
          </div>
        </div>
      )}

      {/* Report (subtle, top-right) */}
      {targetId && !reported && (
        <button
          onClick={() => setReportOpen(true)}
          data-testid={`${testId}-report`}
          aria-label="Report clip"
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 30, height: 30, borderRadius: '50%',
            background: 'rgba(7,7,26,0.65)', backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 3,
          }}
        >
          <Warning size={14} weight="bold" />
        </button>
      )}
      {reported && (
        <p data-testid={`${testId}-reported`} style={{
          position: 'absolute', top: 10, right: 10,
          margin: 0, padding: '4px 8px', borderRadius: 999,
          background: 'rgba(7,7,26,0.7)', color: TEXT_MUTED,
          fontSize: 10.5, fontWeight: 700, fontFamily: FONT_STACK,
          letterSpacing: '0.04em',
        }}>
          Reported ✦
        </p>
      )}

      {overlay && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          padding: 14, color: TEXT, pointerEvents: 'none',
          fontFamily: FONT_STACK,
        }}>
          <div style={{ pointerEvents: 'auto' }}>{overlay}</div>
        </div>
      )}

      {reportOpen && (
        <div
          onClick={() => setReportOpen(false)}
          data-testid={`${testId}-report-confirm`}
          style={{
            position: 'absolute', inset: 0, zIndex: 5,
            background: 'rgba(7,7,26,0.78)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 18,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            maxWidth: 320, width: '100%',
            background: 'rgba(15,15,32,0.96)', borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.08)',
            padding: 18,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Warning size={18} weight="fill" color={PINK} />
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 6px', fontWeight: 900, fontSize: 16, color: TEXT, fontFamily: FONT_STACK }}>
                  Report this clip?
                </p>
                <p style={{ margin: 0, color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.5, fontFamily: FONT_STACK }}>
                  We'll hide it from the feed straight away and review it.
                </p>
              </div>
              <button
                onClick={() => setReportOpen(false)}
                style={{
                  background: 'transparent', border: 'none', color: TEXT_MUTED,
                  cursor: 'pointer', width: 22, height: 22,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                aria-label="Cancel"
              >
                <XIcon size={14} weight="bold" />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={reportClip}
                data-testid={`${testId}-report-yes`}
                style={{
                  flex: 1, padding: '9px 14px', borderRadius: 999, border: 'none',
                  cursor: 'pointer', color: TEXT,
                  background: `linear-gradient(135deg, ${PINK}, #FF7847)`,
                  fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                  boxShadow: `0 6px 16px ${PINK}55`,
                }}
              >
                Report ✦
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewVideoPlayer;
